import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import { Confirm, Notify } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';
import { cancelDelivery, listDeliveries } from '../../../data/api/moveDeliveries/MoveDeliveryRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import DeliveryQrModal from './DeliveryQrModal';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pendiente' },
    { value: 'claimed', label: 'Reclamado' },
    { value: 'expired', label: 'Expirado' },
];

const STATUS_STYLES = {
    pending: { color: '#8a6500', bg: 'rgba(214, 170, 58, 0.18)', border: 'rgba(214, 170, 58, 0.38)' },
    claimed: { color: '#0f5c36', bg: 'rgba(46, 125, 50, 0.12)', border: 'rgba(46, 125, 50, 0.25)' },
    expired: { color: '#9b1c1c', bg: 'rgba(211, 47, 47, 0.10)', border: 'rgba(211, 47, 47, 0.25)' },
};

const STATUS_LABEL = {
    pending: 'Pendiente',
    claimed: 'Reclamado',
    expired: 'Expirado',
};

const DeliveriesHistory = ({ refreshKey = 0 }) => {
    const { token } = useIndex();

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [meta, setMeta] = useState(null);
    const [items, setItems] = useState([]);
    const [load, setLoad] = useState(true);

    const [statusFilter, setStatusFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [docSearch, setDocSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [qrPayload, setQrPayload] = useState(null);

    const buildParams = (p, pp) => {
        const params = { page: p, per_page: pp };
        if (statusFilter) params.status = statusFilter;
        if (methodFilter) params.method = methodFilter;
        if (docSearch) params.recipient_document = docSearch;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        return params;
    };

    const loadData = async (p = page, pp = perPage) => {
        setLoad(true);
        try {
            const { data } = await listDeliveries(token, buildParams(p, pp));
            if (data?.success) {
                setItems(data?.data ?? []);
                setMeta(data?.meta ?? null);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    useEffect(() => {
        loadData(page, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, statusFilter, methodFilter, dateFrom, dateTo, refreshKey]);

    useDebounce(() => {
        if (page !== 1) setPage(1);
        else loadData(1, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 500, [docSearch]);

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangePerPage = (e) => { setPerPage(+e.target.value); setPage(1); };
    const handleChangeFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

    const clearFilters = () => {
        setStatusFilter('');
        setMethodFilter('');
        setDocSearch('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    };

    const askCancel = (uuid) => {
        Confirm.show(
            'Cancelar entrega',
            'Las jugadas reservadas en este QR volverán a tu cuenta. ¿Continuar?',
            'Sí, cancelar',
            'No',
            () => doCancel(uuid),
            () => { },
        );
    };

    const doCancel = async (uuid) => {
        try {
            const { data } = await cancelDelivery(token, uuid);
            if (data?.success) {
                Notify.success(data?.message ?? 'Entrega cancelada.');
                loadData(page, perPage);
            }
        } catch (error) {
            validErrors(error);
        }
    };

    return (
        <>
            <FiltersCard>
                <FiltersGrid>
                    <TextField
                        size="small"
                        placeholder="Buscar por documento del jugador..."
                        value={docSearch}
                        onChange={(e) => setDocSearch(e.target.value)}
                        sx={{ ...fieldStyles, gridColumn: '1 / -1' }}
                    />

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Estado</InputLabel>
                        <Select label="Estado" value={statusFilter} onChange={handleChangeFilter(setStatusFilter)}>
                            <MenuItem value="">Todos</MenuItem>
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Método</InputLabel>
                        <Select label="Método" value={methodFilter} onChange={handleChangeFilter(setMethodFilter)}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="document">Por cédula</MenuItem>
                            <MenuItem value="qr">Por QR</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="date"
                        label="Desde"
                        InputLabelProps={{ shrink: true }}
                        value={dateFrom}
                        onChange={handleChangeFilter(setDateFrom)}
                        sx={fieldStyles}
                    />

                    <TextField
                        size="small"
                        type="date"
                        label="Hasta"
                        InputLabelProps={{ shrink: true }}
                        value={dateTo}
                        onChange={handleChangeFilter(setDateTo)}
                        sx={fieldStyles}
                    />

                    <FormControl size="small" sx={fieldStyles}>
                        <InputLabel>Por página</InputLabel>
                        <Select label="Por página" value={perPage} onChange={handleChangePerPage}>
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </FiltersGrid>

                <Box className="filters-header">
                    <Box className="filters-info">
                        <Chip label={`Total: ${meta?.total ?? 0}`} size="small" />
                        <Chip
                            label={`Página: ${page}/${meta?.last_page ?? 1}`}
                            size="small"
                            variant="outlined"
                        />
                        <Pagination
                            count={meta?.last_page ?? 1}
                            page={page}
                            onChange={handleChangePage}
                            size="small"
                        />
                        <Button size="small" onClick={clearFilters}>Limpiar filtros</Button>
                    </Box>
                </Box>
            </FiltersCard>

            <ListContainer>
                {load ? (
                    [1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={86} sx={{ borderRadius: 2, mb: 1 }} />
                    ))
                ) : items?.length ? (
                    items.map((d) => (
                        <DeliveryRow
                            key={d.uuid}
                            item={d}
                            onCancel={askCancel}
                            onShowQr={(payload) => setQrPayload(payload)}
                        />
                    ))
                ) : (
                    <EmptyState>
                        <FilterAltOutlinedIcon />
                        <Typography className="empty-title">No hay entregas</Typography>
                        <Typography className="empty-text">No se encontraron entregas con los filtros actuales.</Typography>
                    </EmptyState>
                )}
            </ListContainer>

            <DeliveryQrModal
                open={!!qrPayload}
                payload={qrPayload}
                onClose={() => setQrPayload(null)}
            />
        </>
    );
};

export default DeliveriesHistory;

const DeliveryRow = ({ item, onCancel, onShowQr }) => {
    const style = STATUS_STYLES[item.status] ?? STATUS_STYLES.pending;
    const isQr = item.method === 'qr';
    const isPendingQr = isQr && item.status === 'pending' && !!item.claim_url;
    const recipient = item.player
        ? `${item.player.name ?? ''} ${item.player.lastname ?? ''}`.trim()
        : null;

    return (
        <RowCard>
            <Box className="left">
                <Box className="icon-box">
                    {isQr ? <QrCode2OutlinedIcon /> : <BadgeOutlinedIcon />}
                </Box>

                <Box className="info">
                    <Typography className="title">
                        {item.moves} jugadas · {isQr ? 'Por QR' : 'Por cédula'}
                    </Typography>
                    <Typography className="meta">
                        {recipient
                            ? <>Para <strong>{recipient}</strong>{item.recipient_document ? ` · CC ${item.recipient_document}` : ''}</>
                            : item.recipient_document
                                ? <>Pendiente · CC {item.recipient_document}</>
                                : <>Pendiente · QR sin reclamar</>
                        }
                    </Typography>
                    <Typography className="meta meta--small">
                        {item.created_at ? new Date(item.created_at).toLocaleString() : '-'}
                        {item.claimed_at ? ` · reclamado ${new Date(item.claimed_at).toLocaleString()}` : ''}
                        {!item.claimed_at && item.expires_at ? ` · expira ${new Date(item.expires_at).toLocaleString()}` : ''}
                    </Typography>
                </Box>
            </Box>

            <Box className="right">
                <StatusPill $color={style.color} $bg={style.bg} $border={style.border}>
                    {STATUS_LABEL[item.status] ?? item.status}
                </StatusPill>

                {isPendingQr && (
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={() => onShowQr?.(item)}
                        sx={{ color: '#10285f', fontWeight: 800 }}
                    >
                        Ver QR
                    </Button>
                )}

                {item.status === 'pending' && isQr && (
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<CancelOutlinedIcon />}
                        onClick={() => onCancel(item.uuid)}
                        sx={{ color: '#9b1c1c', fontWeight: 800 }}
                    >
                        Cancelar
                    </Button>
                )}
            </Box>
        </RowCard>
    );
};

const FiltersCard = styled.div`
    padding: 0.6rem;
    border-radius: 14px;
    background: #fff;
    margin-bottom: 0.8rem;
    border: 1px solid rgba(15, 34, 77, 0.08);

    .filters-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 0.55rem;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .filters-info {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
    }
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.45rem;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const RowCard = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.7rem 0.85rem;
    border-radius: 14px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 4px 10px rgba(15, 34, 77, 0.04);
    gap: 0.7rem;
    flex-wrap: wrap;

    .left {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        min-width: 0;
        flex: 1;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;
    }

    .info { min-width: 0; }

    .title {
        color: #10285f;
        font-weight: 900;
        font-size: 0.92rem;
        line-height: 1.2;
    }

    .meta {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.78rem;
        font-weight: 600;
        margin-top: 0.15rem;

        strong { color: #10285f; }
    }

    .meta--small { font-size: 0.7rem; }

    .right {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        flex-wrap: wrap;
    }
`;

const StatusPill = styled.span`
    padding: 0.32rem 0.7rem;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 900;
    white-space: nowrap;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
    border: 1px solid ${({ $border }) => $border};
`;

const EmptyState = styled.div`
    min-height: 200px;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    svg { font-size: 2.2rem; color: #d6aa3a; }
    .empty-title { font-weight: 900; font-size: 1.1rem; }
    .empty-text { color: rgba(15, 34, 77, 0.62); max-width: 340px; font-size: 0.86rem; }
`;
